import EXIF from '../lib/exif'
import { batchPromises } from './async'
import ImageNotGeoreferencedError from './image-not-georeferenced-error'
import uuidv4 from 'uuid/v4'
import { proj as findUtmProj } from 'utm-zone'
import proj4 from 'proj4'
import { featureCollection } from '@turf/helpers'
import { CAMERAS } from './camera_utils'

EXIF.enableXmp()

export function flattenXmpData(xmpData) {
  var isString = function (x) {
    return typeof x === 'string' || x instanceof String
  }
  var getValue = function (n) {
    if (isString(n)) {
      return n
    } else if (n['rdf:Seq']) {
      // Not sure how multiple li are represented or what to do then
      return n['rdf:Seq']['rdf:li'] && n['rdf:Seq']['rdf:li']['#text']
    } else if (n['#text']) {
      return n['#text']
    }
  }

  var flatten = function (xmpData) {
    if (xmpData && !isString(xmpData)) {
      return Object.keys(xmpData).reduce(function (a, k) {
        var isValue =
          isString(xmpData[k]) ||
          isString(xmpData[k]['#text']) ||
          xmpData[k]['rdf:Seq']

        if (
          isValue &&
          k !== '#text' &&
          !k.startsWith('xmlns:') &&
          !k.startsWith('x:')
        ) {
          a[k] = getValue(xmpData[k])
        }
        if (!isNaN(k) || k === '@attributes' || k.startsWith('rdf:')) {
          a = Object.assign({}, a, flatten(xmpData[k]))
        }

        return a
      }, {})
    } else {
      return {}
    }
  }

  return flatten(xmpData['x:xmpmeta'])
}

export async function getImageGeoJson(filesOrExif, options) {
  options = options || {}
  let exif
  if (filesOrExif.every(x => x instanceof window.Blob)) {
    exif = await batchPromises(filesOrExif, f => getFileExif(f, false), 1)
  } else {
    exif = filesOrExif
  }

  return {
    type: 'FeatureCollection',
    features: exif.map(e => {
      const mappedExif = exifToProperties(e)
      const hasCoord = mappedExif && mappedExif.latitude && mappedExif.longitude

      if (!hasCoord && !options.allowEmptyGeometry) {
        throw new ImageNotGeoreferencedError(e.filename)
      }

      return {
        type: 'Feature',
        geometry:
          mappedExif && hasCoord
            ? {
                type: 'Point',
                coordinates: [
                  mappedExif.longitude,
                  mappedExif.latitude,
                  mappedExif.absolute_altitude,
                ],
              }
            : null,
        properties: mappedExif || e,
      }
    }),
  }
}

export function getExif(files) {
  return batchPromises(files, f => getFileExif(f, false), 1)
}

export function getFileExif(image, rejectAtErrors = true) {
  return new Promise((resolve, reject) => {
    var reader = new FileReader()
    reader.addEventListener('load', () => {
      var exif
      try {
        exif = EXIF.readFromBinaryFile(reader.result)
        if (exif.xmpData) {
          // Flattening XMP is a non-exact business, so we catch any errors
          // and just warn about them. It's often better to try using the
          // picture without the correctly parsed XMP.
          try {
            const xmpProps = flattenXmpData(exif.xmpData)
            exif = Object.assign({}, exif, xmpProps)
          } catch (e) {
            console.warn('Failed to flatten XMP:', e)
          }
        }
        if (exif) {
          exif.filename = image.name
          resolve(exif)
        } else {
          throw new Error(`No EXIF found in ${image.name}.`)
        }
      } catch (error) {
        if (rejectAtErrors) {
          reject(error)
        } else {
          resolve({ error })
        }
      }
    })
    reader.addEventListener('error', err => {
      const error = new Error(`Failed to get EXIF: ${err}`)
      if (rejectAtErrors) {
        reject(error)
      } else {
        resolve({ error })
      }
    })

    reader.readAsArrayBuffer(image.slice(0, 32 * 1024 * 1024))
  })
}

// Returns a promise that will yield an DOM Image for
// the corresponding image GeoJSON feature; the properties
// of the feature are expected to be in the same format as
// returned by getImageGeoJson above.
export function getImage(image, getImageData, options) {
  options = { revokeObjectURL: true, ...options }
  const mimeType = image.properties.mime || guessMime(image)
  const { orientation } = image.properties
  const data = getImageData(image)

  if (!data) {
    throw new Error('Image data is not available.')
  }

  switch (mimeType) {
    case 'image/tiff':
    case 'image/x-adobe-dng':
      return import('geotiff')
        .then(GeoTIFF =>
          data instanceof Blob ? GeoTIFF.fromBlob(data) : GeoTIFF.fromUrl(data)
        )
        .then(tiff =>
          tiff
            .getImageCount()
            .then(nImages => {
              const maxDimensions = options.maxDimensions || {
                width: Number.MAX_VALUE,
                height: Number.MAX_VALUE,
              }
              return new Promise((resolve, reject) => {
                let imageIndex = 0
                const nextImage = () => {
                  tiff.getImage(imageIndex).then(image => {
                    imageIndex++
                    if (
                      image.getWidth() < maxDimensions.width &&
                      image.getHeight() < maxDimensions.height
                    ) {
                      resolve(image)
                    } else if (imageIndex < nImages) {
                      nextImage()
                    } else {
                      reject(
                        new Error(
                          'No overview with dimension under maxDimensions'
                        )
                      )
                    }
                  })
                }
                nextImage()
              })
            })
            .then(image => tiffImageToRgb(image, options))
        )
    default: {
      const img = new Image()
      img.crossOrigin = options.crossOrigin || 'anonymous'
      if (data instanceof Blob) {
        img.src = window.URL.createObjectURL(data)
        if (options.revokeObjectURL) {
          img.addEventListener('load', () =>
            window.URL.revokeObjectURL(img.src)
          )
        }
      } else {
        img.src = data
      }
      if ((!orientation || orientation === 1) && !options.maxDimensions) {
        return Promise.resolve(img)
      } else {
        // EXIF contains an orientation that requires us to transform
        // the image. The transformation is done in a separate canvas.
        return new Promise((resolve, reject) => {
          img.onerror = e =>
            reject(new Error(`Failed to load image: ${JSON.stringify(e)}`))
          img.addEventListener('load', () => {
            const canvas = scaleAndRotate(
              img,
              orientation,
              options.maxDimensions
            )
            const destImg = new Image()
            destImg.src = canvas.toDataURL()
            resolve(destImg)
          })
        })
      }
    }
  }
}

let pool

function tiffImageToRgb(image, options) {
  const width = image.getWidth()
  const height = image.getHeight()

  let rasterPromise
  let pixelFn

  // TODO: Commented out for now since recent GeoTIFF.js
  // needs threads-plugin to work with pools, and it's not
  // compatible with our version of Webpack (requires version 6):
  // https://github.com/geotiffjs/geotiff.js#using-decoder-pools-to-improve-parsing-performance
  // if (!pool) {
  //   pool = new GeoTIFF.Pool()
  // }

  if (!options.bands || options.bands.slice(0, 3) === 'rgb') {
    rasterPromise = image.readRGB({ pool })
    pixelFn = (src, dst) => {
      for (let i = 0, j = 0; i < width * height * 4; i += 4, j += 3) {
        dst[i] = src[j]
        dst[i + 1] = src[j + 1]
        dst[i + 2] = src[j + 2]
        dst[i + 3] = 255
      }
    }
  } else {
    rasterPromise = image.readRasters({ pool })
    pixelFn = (src, dst) => {
      const rgbIndices = Array.from('rgb')
        .map(b => options.bands.indexOf(b))
        .map(i => (i >= 0 ? i : 0))
      const noData = src[0] instanceof Uint16Array ? 65535 : undefined
      let min = Number.MAX_VALUE
      let max = -Number.MAX_VALUE

      for (let j = 0; j < width * height; j += 1) {
        for (let i = 0; i < rgbIndices.length; i++) {
          const v = src[rgbIndices[i]][j]
          if (v !== noData) {
            min = Math.min(min, v)
            max = Math.max(max, v)
          }
        }
      }

      const w = max - min
      const contrast = options.contrast || 0.2
      min += w * contrast
      max -= w * contrast
      const k = (1 / (max - min)) * 255

      for (let i = 0, j = 0; i < width * height * 4; i += 4, j++) {
        for (let l = 0; l < rgbIndices.length; l++) {
          dst[i + l] = (src[rgbIndices[l]][j] - min) * k
        }

        dst[i + 3] = 255
      }
    }
  }

  return rasterPromise.then(rasters => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const rgba = new Uint8ClampedArray(width * height * 4)
    pixelFn(rasters, rgba)
    canvas
      .getContext('2d')
      .putImageData(new ImageData(rgba, canvas.width, canvas.height), 0, 0)
    const img = new Image()
    img.src = canvas.toDataURL()

    return img
  })
}

/// Given an Image, and optionally its EXIF orientation as well as maximum
/// output dimensions, returns a Canvas with the image correctly rotated and
/// scaled.
export function scaleAndRotate(img, orientation, maxDimensions) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const originalDimensions = {
    width: img.naturalWidth,
    height: img.naturalHeight,
  }
  orientation = orientation || 1

  let { width, height } = maxDimensions
    ? getScaledDimensions(originalDimensions, maxDimensions)
    : originalDimensions
  if (orientation >= 5) {
    ;[height, width] = [width, height]
  }
  canvas.width = width
  canvas.height = height

  // See https://magnushoff.com/articles/jpeg-orientation/
  // for an explanation on what is going on here.
  const flipDiagonal = [0, 1, 1, 0, 0, 0]
  const rotate180 = [-1, 0, 0, -1, width, height]
  const flipHorizontal = [-1, 0, 0, 1, width, 0]
  const orientationBits = orientation - 1
  if (orientationBits & 1) {
    ctx.transform.apply(ctx, flipHorizontal)
  }
  if (orientationBits & 2) {
    ctx.transform.apply(ctx, rotate180)
  }
  if (orientationBits & 4) {
    ctx.transform.apply(ctx, flipDiagonal)
  }

  ctx.drawImage(img, 0, 0, width, height)

  return canvas
}

function exifToProperties(exif) {
  if (exif.error) {
    return exif
  }

  try {
    const exifCoordToDecimal = x =>
      x[0].valueOf() + x[1].valueOf() / 60 + x[2].valueOf() / 3600
    const coord = exif.GPSLongitude &&
      exif.GPSLatitude && [
        exifCoordToDecimal(exif.GPSLongitude) *
          (exif.GPSLongitudeRef.toLowerCase() === 'e' ? 1 : -1),
        exifCoordToDecimal(exif.GPSLatitude) *
          (exif.GPSLatitudeRef.toLowerCase() === 'n' ? 1 : -1),
      ]
    const exifDateTimeToIso8601 = s => {
      const [date, time] = s.split(' ')
      return date.split(':').join('-') + ' ' + time
    }

    const relativeAltitude = exif['drone-dji:RelativeAltitude']

    const cameraOrientation = {
      camera_roll: Number(exif['drone-dji:GimbalRollDegree']),
      camera_pitch: Number(exif['drone-dji:GimbalPitchDegree']),
      camera_yaw: Number(exif['drone-dji:GimbalYawDegree']),
    }
    const orientationValues = Object.values(cameraOrientation)
    const hasCameraOrientation =
      orientationValues.every(v => !isNaN(v)) &&
      // Some drones (at least DJI FC3682) has all properties but they are all _always_ set to exactly 0
      orientationValues.some(v => v !== 0)

    return {
      make: exif.Make && exif.Make.replace(/\0/g, '').trim(),
      model: exif.Model && exif.Model.replace(/\0/g, '').trim(),
      width: exif.ImageWidth || exif.ExifImageWidth || exif.PixelXDimension,
      height:
        exif.ImageHeight ||
        exif.ExifImageHeight ||
        exif.ImageLength ||
        exif.ExifImageLength ||
        exif.PixelYDimension,
      filename: exif.filename,
      datetime:
        (exif.DateTimeOriginal &&
          exifDateTimeToIso8601(exif.DateTimeOriginal)) ||
        (exif.DateTime && exifDateTimeToIso8601(exif.DateTime)),
      orientation: exif.Orientation,
      absolute_altitude: exif.GPSAltitude && exif.GPSAltitude.valueOf(),
      relative_altitude: relativeAltitude && Number(relativeAltitude),
      ...(coord && {
        latitude: coord[1],
        longitude: coord[0],
      }),
      capture_id: getCaptureId(exif),
      bandName: exif['Camera:BandName'],
      calibration_picture: exif['Camera:CalibrationPicture'] > 0,
      focal_length: exif.FocalLength,
      ...(hasCameraOrientation ? cameraOrientation : undefined),
    }
  } catch (error) {
    return {
      error,
    }
  }
}

const djiThermalModels = ['M30T', 'M3T', 'ZH20T']
// We have not figured out how to get a capture id from EXIF for
// DJI's thermal cameras, so we're left with using the image count
// of the image, which isn't perfect; in particular, this sometimes
// isn't unique (for different reasons the count is reset during a survey);
// we later ensure correct grouping by not grouping images with timestamps
// too far apart into the same capture.
const djiThermalCaptureIdPattern = new RegExp('.*_(\\d+)_[VWT][^A-Za-z0-9]')

function getCaptureId(exif) {
  const gpsTimeStampToString = x => x.map(y => Number(y)).join(':')

  let captureId

  if (exif) {
    if (djiThermalModels.indexOf(exif.Model) >= 0) {
      captureId = djiThermalCaptureIdPattern.exec(exif.filename)[1]
    } else {
      captureId =
        exif['MicaSense:CaptureId'] ||
        exif['Camera:CaptureUUID'] ||
        exif['drone-dji:CaptureUUID'] ||
        (exif.GPSTimeStamp && gpsTimeStampToString(exif.GPSTimeStamp)) ||
        exif.DateTimeOriginal ||
        exif.DateTime ||
        exif.filename
    }
  }

  if (captureId) {
    return captureId
  } else {
    return uuidv4()
  }
}

function guessMime(image) {
  const parts = image.properties.filename.split('.')
  const ext = parts[parts.length - 1]
  switch (ext.toLowerCase()) {
    case 'tif':
    case 'tiff':
      return 'image/tiff'
    case 'dng':
      return 'image/x-adobe-dng'
    default:
      return null
  }
}

function getScaledDimensions(
  { width: origWidth, height: origHeight },
  { width: maxWidth, height: maxHeight }
) {
  const scaleFactor = Math.min(maxWidth / origWidth, maxHeight / origHeight, 1)

  return {
    width: origWidth * scaleFactor,
    height: origHeight * scaleFactor,
  }
}

export function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = e =>
      reject(new Error(`Failed to load image: ${JSON.stringify(e)}`))
    img.src = url
  })
}

export async function copyExif(src, dest) {
  const exif = await retrieveExif(src)
  return new Blob([dest.slice(0, 2), exif, dest.slice(2)], {
    type: 'image/jpeg',
  })
}

const SOS = 0xffda
const APP1 = 0xffe1
const EXIFTag = 0x45786966
const retrieveExif = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', ({ target: { result: buffer } }) => {
      const view = new DataView(buffer)
      let offset = 0
      if (view.getUint16(offset) !== 0xffd8)
        return reject(new Error('not a valid jpeg'))
      offset += 2

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const marker = view.getUint16(offset)
        if (marker === SOS) break
        const size = view.getUint16(offset + 2)
        if (marker === APP1 && view.getUint32(offset + 4) === EXIFTag)
          return resolve(blob.slice(offset, offset + 2 + size))
        offset += 2 + size
      }
      return resolve(new Blob())
    })
    reader.readAsArrayBuffer(blob)
  })

export function getImageFootprints(imageGeojson) {
  if (!imageGeojson || !imageGeojson.features) {
    throw new Error('Expected GeoJSON FeatureCollection')
  }
  if (!imageGeojson.features.length) {
    return featureCollection([])
  }

  const { model, make } = imageGeojson.features[0].properties
  const cameraName = `${make} ${model}`
  const camera = CAMERAS[cameraName]

  if (!camera) {
    throw new Error(`Unknown camera: ${cameraName}`)
  } else if (!camera.sensor) {
    throw new Error(
      `Camera ${cameraName} has no sensor dimensions in our database.`
    )
  }

  const utmProj = findUtmProj(imageGeojson)
  const proj = proj4(utmProj)

  const footprints = imageGeojson.features
    .filter(
      ({
        properties: {
          relative_altitude: relativeAltitude,
          camera_yaw: cameraYaw,
          focal_length: focalLength,
        },
      }) => relativeAltitude != null && cameraYaw != null && focalLength != null
    )
    .map(feature => ({
      type: 'Feature',
      properties: feature.properties,
      geometry: getImageFootprintPolygon(
        feature.geometry.coordinates,
        feature.properties.relative_altitude,
        feature.properties.camera_yaw,
        feature.properties.focal_length,
        camera.sensor,
        proj
      ),
    }))

  return featureCollection(footprints)
}

/**
 * Calculates the footprint (dimensions in meters on ground) from a picture taken
 * with a specific camera at the given altitude.
 *
 * @param {number} focalLength focal length in millimeters
 * @param {number} altitude in meters
 * @param {[number, number]} sensorDimensions width and height of camera sensor i millimeters
 * @returns image width and height in meters
 */
export function getImageFootprintDimensions(
  focalLength,
  altitude,
  sensorDimensions
) {
  const halfFovs = sensorDimensions.map(d => Math.atan(d / 2 / focalLength))
  return halfFovs.map(halfFov => 2 * altitude * Math.tan(halfFov))
}

function rotate(c, theta) {
  const [x, y] = c
  const cos = Math.cos(theta)
  const sin = Math.sin(theta)
  return [x * cos - y * sin, x * sin + y * cos]
}

// See https://gis.stackexchange.com/questions/384756/georeference-single-drone-image-from-exif-data/389087#389087
function getImageFootprintPolygon(
  center,
  altitude,
  yaw,
  focalLength,
  sensorDimensions,
  projection
) {
  const theta = (-yaw / 180) * Math.PI
  const utmCenter = projection.forward(center)
  const [halfWidth, halfHeight] = getImageFootprintDimensions(
    focalLength,
    altitude,
    sensorDimensions
  ).map(x => x / 2)
  const corners = [
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
  ]
  const rotatedCorners = corners.map(c => rotate(c, theta))
  const unprojectedCorners = rotatedCorners.map(c =>
    projection.inverse([c[0] + utmCenter[0], c[1] + utmCenter[1]])
  )
  return {
    type: 'Polygon',
    coordinates: [unprojectedCorners],
  }
}
